import { Suspense, lazy, useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useSelector } from "react-redux";
import {
  initChatSocket,
  getChatList,
} from "src/Redux/Users/Chat/userChatActions";
import { getGroupList } from "src/Redux/Users/GroupChat/groupChatActions";
import store from "src/Redux/Store";
import { ErrorBoundary } from "react-error-boundary";
import { recoverFromRuntimeAssetError } from "./utils/runtimeAssetRecovery";
import ContextProvider from "./Context/ContextProvider";
import SuspenseLoading from "./Components/UI/SuspenseLoading/SuspenseLoading";
import ErorBoundry from "./Components/UI/ErorBoundry/ErorBoundry";
import IncomingCallModal from "./Components/Chat/IncomingCallModal";
import AlarmPopup from "./Components/UI/Alarm/AlarmPopup";
import PushNotificationPrompt from "./Components/PushNotifications/PushNotificationPrompt";
const GlobalSearchMountLazy = lazy(() =>
  import("./Components/GlobalSearch/GlobalSearchPalette").then((m) => ({
    default: m.GlobalSearchMount,
  })),
);
const MyApprovalsLazy = lazy(
  () => import("./Components/GlobalFloatingBadges/MyApprovals/MyApprovals"),
);
import { setToastNavigate } from "./Funcs/toastNavigate";
import { getSocketUserChat } from "./Funcs/APIFuncs/socketUserChat";
import { AlarmProvider, useAlarm } from "./Context/AlarmContext";

const NotFound = lazy(() => import("./Pages/NotFound"));
const RateLimitError = lazy(() => import("./Pages/RateLimitError"));
import { useRTL } from "./Hooks/useRTL";
import { RequireAuth, BoardGate } from "./Components/UI/AuthGate";
import { isPublicPath } from "./Funcs/auth/routePolicy";
import { isAuthenticated } from "./Funcs/auth/authStore";
import PagePrefetch from "./Components/PagePrefetch/PagePrefetch";
import AppUpdateChecker from "./Components/UI/AppUpdate/AppUpdateChecker";
import WebUpdateBanner from "./Components/UI/AppUpdate/WebUpdateBanner";
import { TourProvider, TourRunner } from "./Components/UI/Tour";
import "./styles/table.css";

const Login = lazy(() => import("./Pages/Login"));
const DashboardLayout = lazy(
  () => import("./Components/Dashboard/1/DashboardLayout"),
);
const Board = lazy(() => import(/* webpackPrefetch: true */ "./Pages/Board"));
const Register = lazy(() => import("./Pages/Register"));
const UnsuccessPayment = lazy(() => import("./Pages/UnsuccessPayment"));
const SuccessPayment = lazy(() => import("./Pages/SuccessPayment"));
const Integrations = lazy(() => import("./Pages/Integrations"));
const FormPageRedirect = lazy(() => import("./Pages/Publics/FormPageRedirect"));
const Home = lazy(() => import("./Pages/Home"));
const Chat = lazy(() => import("./Pages/Chat"));
const AIStudioPage = lazy(
  () => import(/* webpackPrefetch: true */ "./Pages/AIStudioPage"),
);
const AIEmployerWorkspacePage = lazy(
  () => import("./Pages/AIEmployerWorkspace/AIEmployerWorkspacePage"),
);
const PublicTaskPage = lazy(() => import("./Pages/PublicTaskPage"));
const PublicReportPage = lazy(() => import("./Pages/PublicReportPage"));
const PublicPricingPage = lazy(
  () => import("./Pages/Publics/PublicPricingPage"),
);
const AdminPanel = lazy(() => import("./Pages/AdminPanel"));
const MarketplacePage = lazy(() => import("./Pages/MarketplacePage"));
const CopilotPage = lazy(() => import("./Pages/CopilotPage"));
const CoachPage = lazy(() => import("./Pages/CoachPage"));
const MeetPage = lazy(() => import("./Pages/MeetPage"));
const PomodoroPopupPage = lazy(
  () => import("./Components/Board/1/Pomodoro/PomodoroPopupPage"),
);
const MoriTest = lazy(() => import("./Pages/MoriTest"));
const GroupJoinPage = lazy(() => import("./Pages/GroupJoinPage"));
const BoardJoinPage = lazy(() => import("./Pages/BoardJoinPage"));
const GlobalFloatingBadgesLazy = lazy(
  () => import("./Components/GlobalFloatingBadges/GlobalFloatingBadges"),
);

const DashboardHubRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/DashboardHubRoute"),
);
const ProjectOverviewRoute = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "./Components/Dashboard/1/tabs/ProjectOverviewRoute"
    ),
);
const BoardsListRoute = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "./Components/Dashboard/1/tabs/BoardsListRoute"
    ),
);
const HomeFeedRoute = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "./Components/Dashboard/1/tabs/HomeFeedRoute"
    ),
);
const SearchUsersRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/SearchUsersRoute"),
);
const DiscoverRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/DiscoverRoute"),
);
const ProfileRoute = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "./Components/Dashboard/1/tabs/ProfileRoute"
    ),
);
const ChatsRoute = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "./Components/Dashboard/1/tabs/ChatsRoute"
    ),
);
const GroupsRoute = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "./Components/Dashboard/1/tabs/GroupsRoute"
    ),
);
const AIStudioRoute = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "./Components/Dashboard/1/tabs/AIStudioRoute"
    ),
);
const ProgramHubRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/ProgramHubRoute"),
);
const PersonalTaskRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/PersonalTaskRoute"),
);
const ToolsRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/ToolsRoute"),
);
const VoiceRecorderRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/VoiceRecorderRoute"),
);
const SpeechToTextRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/SpeechToTextRoute"),
);
const MeetingRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/MeetingRoute"),
);
const UserInfoRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/UserInfoRoute"),
);
const NotificationRoute = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "./Components/Dashboard/1/tabs/NotificationRoute"
    ),
);
const PaymentsRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/PaymentsRoute"),
);
const AffiliateRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/AffiliateRoute"),
);
const CoachRoute = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "./Components/Dashboard/1/tabs/CoachRoute"
    ),
);
const DailyReportRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/DailyReportRoute"),
);
const HistoryRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/HistoryRoute"),
);
const SettingsRoute = lazy(
  () => import("./Components/Dashboard/1/tabs/SettingsRoute"),
);
const McpOAuthReturnRoute = lazy(
  () => import("./Components/AIResources/capabilities/mcp/McpOAuthReturnRoute"),
);

function ChatSocketInit() {
  const location = useLocation();
  const didInit = useRef(false);

  // Only initialize chat socket on chat-related routes
  const isChatRoute =
    location.pathname.startsWith("/chat") ||
    location.pathname.startsWith("/meet") ||
    location.pathname === "/home";

  useEffect(() => {
    if (!localStorage.getItem("UserID")) return;
    if (!isChatRoute) return;
    initChatSocket();
    store.dispatch(getChatList({ page: 1, limit: 20 }));
    store.dispatch(getGroupList({ page: 1, limit: 20 }));
  }, []);

  useEffect(() => {
    if (isPublicPath(location.pathname)) return;
    if (!localStorage.getItem("UserID")) return;
    if (!isChatRoute) return;
    if (didInit.current) return;
    didInit.current = true;
    initChatSocket();
    store.dispatch(getChatList({ page: 1, limit: 20 }));
    store.dispatch(getGroupList({ page: 1, limit: 20 }));
  }, [location.pathname]);

  return null;
}

function GlobalIncomingCall() {
  const location = useLocation();
  const incomingCall = useSelector((state) => state.userChat?.incomingCall);

  // Only show incoming call on authenticated routes
  if (!incomingCall?.chatId || isPublicPath(location.pathname)) return null;
  return <IncomingCallModal incomingCall={incomingCall} />;
}

function AlarmSocketInit() {
  const location = useLocation();

  useEffect(() => {
    if (!localStorage.getItem("UserID")) return;
    if (isPublicPath(location.pathname)) return;
    getSocketUserChat();
  }, [location.pathname]);

  return null;
}

function AlarmPopupMount() {
  const location = useLocation();
  const ctx = useAlarm();
  const alarm = ctx?.alarm;

  if (!alarm || isPublicPath(location.pathname)) return null;
  return <AlarmPopup />;
}

function ToastNavigateInit() {
  const navigate = useNavigate();
  useEffect(() => {
    setToastNavigate(navigate);
    return () => setToastNavigate(null);
  }, [navigate]);
  return null;
}

function ConditionalGlobalSearch() {
  const location = useLocation();

  // Only load global search for signed-in users on authenticated routes
  if (!isAuthenticated() || isPublicPath(location.pathname)) return null;
  return (
    <Suspense fallback={null}>
      <GlobalSearchMountLazy />
      <MyApprovalsLazy />
    </Suspense>
  );
}

function ConditionalGlobalFloatingBadges() {
  const location = useLocation();
  if (!isAuthenticated() || isPublicPath(location.pathname)) return null;
  return <GlobalFloatingBadgesLazy />;
}

const App = () => {
  const Lang = localStorage.getItem("SiteLanguage");
  const Theme = localStorage.getItem("theme");
  const rootElement = document.querySelector("html");
  const isRTL = useRTL();
  useEffect(() => {
    const Condition = Lang === "fa" || Lang === "ar";

    // RTL یا LTR
    if (Condition) {
      rootElement.classList.add("rtl-Lang");
      rootElement.classList.remove("ltr-Lang");
    } else {
      rootElement.classList.add("ltr-Lang");
      rootElement.classList.remove("rtl-Lang");
    }

    // فونت فارسی یا انگلیسی
    if (Condition) {
      rootElement.classList.add("font-fa");
      rootElement.classList.remove("font-en");
    } else {
      rootElement.classList.add("font-en");
      rootElement.classList.remove("font-fa");
    }
  }, [Lang, isRTL]);

  // useEffect(() => {
  //   const Condition = Lang === "fa" || Lang === "ar";

  //   if (Condition) {
  //     rootElement.classList.add("rtl-Lang");
  //   } else {
  //     rootElement.classList.remove("rtl-Lang");
  //   }
  // }, [Lang]);

  const UserSettings = useSelector((state) => state.UserSettings);

  const DefaultTheme = UserSettings.Theme;
  const CustomizedThemePallet = UserSettings.CustomizedThemePallet;

  useEffect(() => {
    if (
      window.location.pathname === "/" ||
      window.location.pathname === "/pricing"
    ) {
      return rootElement.setAttribute(
        "theme",
        import.meta.env.VITE_DEFAULT_THEME || "DarkMode4",
      );
    }

    // console.log("im in calc theme...");

    // console.log({
    //   Theme,
    //   DefaultTheme,
    //   CustomizedThemePallet,
    // });

    if (!Theme && !DefaultTheme) return;
    rootElement.setAttribute("theme", Theme);
    if (DefaultTheme) {
      localStorage.setItem("theme", DefaultTheme);
      rootElement.setAttribute("theme", DefaultTheme);
    }

    if (DefaultTheme === "CustomizedTheme") ChangeCSSColorVariableHandler();
    else ResetCSSColorVariables();
  }, [Theme, DefaultTheme, CustomizedThemePallet]);

  const ChangeCSSColorVariableHandler = () => {
    const ColorHandler = (color) => {
      return `rgba(${color.r},${color.g},${color.b},${color.a})`;
    };

    const doc = document.documentElement.style;

    doc.setProperty("--black", ColorHandler(CustomizedThemePallet.black));
    doc.setProperty("--white", ColorHandler(CustomizedThemePallet.white));
    doc.setProperty(
      "--lightGray",
      ColorHandler(CustomizedThemePallet.lightGray),
    );
    doc.setProperty("--darkGray", ColorHandler(CustomizedThemePallet.darkGray));
    doc.setProperty("--lessDark", ColorHandler(CustomizedThemePallet.lessDark));
    doc.setProperty("--purple", ColorHandler(CustomizedThemePallet.purple));
    doc.setProperty(
      "--bgLightGray",
      ColorHandler(CustomizedThemePallet.bgLightGray),
    );
    doc.setProperty(
      "--bgDarkGray",
      ColorHandler(CustomizedThemePallet.bgDarkGray),
    );
    doc.setProperty("--bgPurple", ColorHandler(CustomizedThemePallet.bgPurple));
  };
  //hh
  const ResetCSSColorVariables = () => {
    const doc = document.documentElement.style;
    doc.removeProperty("--black");
    doc.removeProperty("--white");
    doc.removeProperty("--lightGray");
    doc.removeProperty("--darkGray");
    doc.removeProperty("--lessDark");
    doc.removeProperty("--purple");
    doc.removeProperty("--bgLightGray");
    doc.removeProperty("--bgDarkGray");
    doc.removeProperty("--bgPurple");
  };

  function ErrorFallback({ error, resetErrorBoundary }) {
    return (
      <div>
        <h2>Something went wrong:</h2>
        <pre style={{ whiteSpace: "pre-wrap" }}>{error.message}</pre>
        <button onClick={resetErrorBoundary}>Try Again</button>
      </div>
    );
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErorBoundry}
      onError={recoverFromRuntimeAssetError}
      onReset={() => window.location.reload()}
    >
      <AlarmProvider>
        <BrowserRouter>
          <TourProvider>
            <ContextProvider>
              <TourRunner />
              <ToastNavigateInit />
              <ChatSocketInit />
              <GlobalIncomingCall />
              <AlarmSocketInit />
              <AlarmPopupMount />
              <PushNotificationPrompt />
              <ConditionalGlobalSearch />
              <ConditionalGlobalFloatingBadges />
              <AppUpdateChecker />
              <WebUpdateBanner />
              <PagePrefetch />
              <Suspense fallback={<SuspenseLoading />}>
                <Routes>
                  {/* Public routes — see src/Funcs/auth/routePolicy.js */}
                  <Route path="/" element={<Login />} />
                  <Route path="/Register" element={<Register />} />
                  <Route path="/pricing" element={<PublicPricingPage />} />
                  <Route
                    path="/rate-limit-error"
                    element={<RateLimitError />}
                  />
                  <Route
                    path="/public/task/:boardId/:taskId"
                    element={<PublicTaskPage />}
                  />
                  <Route
                    path="/public/report/:token"
                    element={<PublicReportPage />}
                  />
                  <Route
                    path="/group/join/:linkId"
                    element={<GroupJoinPage />}
                  />

                  {/* Board: public boards viewable anonymously, private
                      boards need session + membership (decided in BoardGate
                      before the Board chunk loads). */}
                  <Route
                    path="/Board"
                    element={
                      <BoardGate>
                        <Board />
                      </BoardGate>
                    }
                  />

                  {/* Protected routes — RequireAuth gates rendering and
                      redirects to /?redirect=... before the page loads. */}
                  <Route element={<RequireAuth />}>
                    <Route path="/Dashboard" element={<DashboardLayout />}>
                      <Route index element={<DashboardHubRoute />} />
                      <Route
                        path="projects"
                        element={<ProjectOverviewRoute />}
                      />
                      <Route path="board" element={<BoardsListRoute />} />
                      <Route path="feed" element={<HomeFeedRoute />} />
                      <Route path="search" element={<SearchUsersRoute />} />
                      <Route path="discover" element={<DiscoverRoute />} />
                      <Route path="profile" element={<ProfileRoute />} />
                      <Route path="chats" element={<ChatsRoute />} />
                      <Route path="groups" element={<GroupsRoute />} />
                      <Route path="ai-studio" element={<AIStudioRoute />} />
                      <Route path="programs" element={<ProgramHubRoute />} />
                      <Route path="tasks" element={<PersonalTaskRoute />} />
                      <Route path="tools" element={<ToolsRoute />} />
                      <Route
                        path="tools/voice"
                        element={<VoiceRecorderRoute />}
                      />
                      <Route
                        path="tools/speech"
                        element={<SpeechToTextRoute />}
                      />
                      <Route path="meeting" element={<MeetingRoute />} />
                      <Route
                        path="notifications"
                        element={<NotificationRoute />}
                      />
                      <Route path="payments" element={<PaymentsRoute />} />
                      <Route path="affiliate" element={<AffiliateRoute />} />
                      <Route path="coach" element={<CoachRoute />} />
                      <Route
                        path="daily-report"
                        element={<DailyReportRoute />}
                      />
                      <Route path="history" element={<HistoryRoute />} />
                      <Route path="info" element={<UserInfoRoute />} />
                      <Route path="settings" element={<SettingsRoute />} />
                      {/* OAuth redirect target for MCP server connections (backend redirects here after the IdP callback). */}
                      <Route
                        path="settings/ai-resources"
                        element={<McpOAuthReturnRoute />}
                      />
                      <Route path="*" element={<DashboardHubRoute />} />
                    </Route>
                    <Route path="/AIStudio" element={<AIStudioPage />} />
                    <Route
                      path="/ai-employer"
                      element={<AIEmployerWorkspacePage />}
                    />
                    <Route path="/copilot" element={<CopilotPage />} />
                    <Route path="/coach" element={<CoachPage />} />
                    <Route
                      path="/UnsuccessPayment"
                      element={<UnsuccessPayment />}
                    />
                    <Route
                      path="/SuccessPayment"
                      element={<SuccessPayment />}
                    />
                    <Route path="/integrations" element={<Integrations />} />
                    <Route path="/moriTest" element={<MoriTest />} />
                    <Route path="/meet" element={<MeetPage />} />
                    <Route path="/meet/:roomId" element={<MeetPage />} />
                    <Route
                      path="/Board/pomodoro-popup/:boardId?"
                      element={<PomodoroPopupPage />}
                    />
                    {/* <Route path="/screen-recorder" element={<ScreenRecorderPg />} /> */}
                    {/* <Route path="/form/:formId" element={<FormPageRedirect />} /> */}
                    <Route path="/home" element={<Home />} />
                    <Route path="/admin" element={<AdminPanel />} />
                    {import.meta.env.DEV ? (
                      <Route
                        path="/marketplace"
                        element={<MarketplacePage />}
                      />
                    ) : null}
                    <Route path="/chat" element={<Chat />} />
                    <Route
                      path="/board/join/:boardID"
                      element={<BoardJoinPage />}
                    />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ContextProvider>
          </TourProvider>
        </BrowserRouter>
      </AlarmProvider>
    </ErrorBoundary>
  );
};

export default App;
